import { Locale } from 'date-fns'
import { enUS } from 'date-fns/locale'

interface CustomLocaleOptions {
  locale: string
  translations: any
  isRtl: boolean
}

/**
 * Creates a custom locale for DatePicker based on project translations
 * @param options - options for creating the locale
 * @returns custom locale for date-fns
 */
export const createCustomDatePickerLocale = (options: CustomLocaleOptions): Locale => {
  const { locale, translations, isRtl } = options

  // Function to get the translation
  const getTranslation = (key: string): string => {
    return translations[locale]?.[key] || translations['base']?.[key] || key
  }

  // Create the base locale based on enUS
  const customLocale: Locale = {
    ...enUS,
    localize: {
      ...enUS.localize,
      // Months
      month: (n: number) => {
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        return getTranslation(monthKeys[n]) || enUS.localize?.month(n) || ''
      },
      // Days of the week
      day: (n: number) => {
        const dayKeys = ['t_sun', 't_mon', 't_tue', 't_wed', 't_thu', 't_fry', 't_sat']
        return getTranslation(dayKeys[n]) || enUS.localize?.day(n) || ''
      },
      // Day periods
      dayPeriod: (n: number) => {
        const periodKeys = ['am', 'pm']
        return getTranslation(periodKeys[n]) || enUS.localize?.dayPeriod(n) || ''
      },
      // Required fields for localize
      ordinalNumber: (...args: any[]) => {
        return enUS.localize?.ordinalNumber(...args) || ''
      },
      era: (...args: any[]) => {
        return enUS.localize?.era(...args) || ''
      },
      quarter: (...args: any[]) => {
        return enUS.localize?.quarter(...args) || ''
      },
    },
    // Formatting
    formatLong: {
      ...enUS.formatLong,
      date: (options: any) => {
        return enUS.formatLong?.date(options) || ''
      },
      time: (options: any) => {
        return enUS.formatLong?.time(options) || ''
      },
      dateTime: (options: any) => {
        return enUS.formatLong?.dateTime(options) || ''
      },
    },
    // Formatting options
    formatRelative: (token: string, date: Date, baseDate: Date, options: any) => {
      // Override relative dates
      const relativeKeys: { [key: string]: string } = {
        lastWeek: 'last_week',
        yesterday: 'yesterday',
        today: 'today',
        tomorrow: 'tomorrow',
        nextWeek: 'next_week',
        other: 'other',
      }

      const translationKey = relativeKeys[token]
      if (translationKey) {
        return getTranslation(translationKey)
      }

      return enUS.formatRelative?.(token, date, baseDate, options) || ''
    },
    // Parsing options
    match: {
      ...enUS.match,
      ordinalNumber: (n: number, options: any) => {
        return enUS.match?.ordinalNumber(n, options) || ''
      },
      era: (n: number, options: any) => {
        return enUS.match?.era(n, options) || ''
      },
      quarter: (n: number, options: any) => {
        const quarterKeys = ['q1', 'q2', 'q3', 'q4']
        return getTranslation(quarterKeys[n - 1]) || enUS.match?.quarter(n, options) || ''
      },
      month: (n: number, options: any) => {
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        return getTranslation(monthKeys[n - 1]) || enUS.match?.month(n, options) || ''
      },
      day: (n: number, options: any) => {
        const dayKeys = ['t_sun', 't_mon', 't_tue', 't_wed', 't_thu', 't_fry', 't_sat']
        return getTranslation(dayKeys[n]) || enUS.match?.day(n, options) || ''
      },
      dayPeriod: (n: number, options: any) => {
        const periodKeys = ['am', 'pm']
        return getTranslation(periodKeys[n]) || enUS.match?.dayPeriod(n, options) || ''
      },
    },
    // Options
    options: {
      ...enUS.options,
      weekStartsOn: isRtl ? 0 : 1,
      firstWeekContainsDate: 4,
    },
  }

  return customLocale
}
